/**
 * Utility for exporting tabular data to CSV format
 */

import {ApplicationState} from "framework/applicationState";
import moment from "moment/moment";

/**
 * Interface for column definition
 */
export interface ColumnDefinition {
	field: string;
	title: string;
	format?: (value: any) => string;
}

/**
 * Options for CSV export
 */
export interface CSVExportOptions {
	fileName?: string;
	delimiter?: string;
	includeHeader?: boolean;
	encoding?: string;
	excludeColumns?: string[];
	selectedRows?: any[];
	convertTimestampsToDateTime?: boolean;
	timeZone?: string;
}

/**
 * Exports tabular data to a CSV file
 * @param data Array of data objects to export
 * @param columns Column definitions that specify which fields to export and how to format them
 * @param options Export options like filename, delimiter, etc.
 */
export function exportToCSV(
	data: any[],
	columns: ColumnDefinition[],
	options: CSVExportOptions = {}
): void {
	// Default options
	const defaultOptions: CSVExportOptions = {
		fileName: 'export.csv',
		delimiter: ',',
		includeHeader: true,
		encoding: 'utf-8',
		convertTimestampsToDateTime: true,
		timeZone: ApplicationState.timezone
	};

	const exportOptions = {...defaultOptions, ...options};
	const {fileName, delimiter, includeHeader, excludeColumns, convertTimestampsToDateTime, timeZone} = exportOptions;

	// Filter columns if excludeColumns is provided
	let filteredColumns = (excludeColumns?.length
		? columns.filter(col => !excludeColumns.includes(col.field))
		: columns).filter(col => !!col.field) as ColumnDefinition[];

	// Detect timestamp columns and add date conversion columns if needed
	if (convertTimestampsToDateTime && data.length > 0) {
		const newColumns: ColumnDefinition[] = [];

		filteredColumns.forEach(col => {
			newColumns.push(col);
			// Check if any values in this column are timestamps
			const hasTimestamps = data.some(item => {
				const value = col.field.includes('.')
					? getNestedValue(item, col.field)
					: item[col.field];
				return isTimestamp(value);
			});

			if (hasTimestamps) {
				// Add a new column for the converted datetime
				const dateTimeColumn: ColumnDefinition = {
					field: col.field + '_datetime',
					title: col.title + ' (DateTime)',
					format: (value: any) => {
						if (isTimestamp(value)) {
							const isMs = value >= 1e12;
							const ms = isMs ? value : value * 1000;
							return moment(ms).tz(timeZone).format(ApplicationState.momentDateTimeFormatFull);
						}
						return value;
					}
				};
				newColumns.push(dateTimeColumn);
			}
		});

		filteredColumns = newColumns;
	}


	// Generate the CSV content
	let csvContent = '';

	// Add header row
	if (includeHeader) {
		const headerRow = filteredColumns.map(col => `"${escapeCSV(col.title)}"`).join(delimiter);
		csvContent += headerRow + '\r\n';
	}

	// Add data rows
	data.forEach(item => {
		const row = filteredColumns.map(col => {
			let value;

			// Handle datetime conversion columns
			if (col.field.endsWith('_datetime') && convertTimestampsToDateTime) {
				const originalField = col.field.replace('_datetime', '');
				value = originalField.includes('.')
					? getNestedValue(item, originalField)
					: item[originalField];
			} else {
				value = col.field.includes('.')
					? getNestedValue(item, col.field)
					: item[col.field];
			}

			const formattedValue = col.format
				? col.format(value)
				: value;
			return `"${escapeCSV(formattedValue)}"`;
		}).join(delimiter);

		csvContent += row + '\r\n';
	});

	// Create and download the file
	downloadCSV(csvContent, fileName!, exportOptions.encoding!);
}

/**
 * Escape special characters in CSV fields
 */
function escapeCSV(value: any): string {
	if (value === null || value === undefined) {
		return '';
	}

	const stringValue = String(value);
	return stringValue.replace(/"/g, '""');
}

/**
 * Get nested object value using dot notation
 */
function getNestedValue(obj: any, path: string): any {
	return path.split('.').reduce((prev, curr) => {
		return prev ? prev[curr] : null;
	}, obj);
}

function isTimestamp(value: number) {
	if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
		return false;
	}

	const isMs = value >= 1e12;
	const ms = isMs ? value : value * 1000;
	const minMs = Date.UTC(2000, 0, 1);
	const maxMs = Date.UTC(3000, 0, 1);
	if (ms < minMs || ms > maxMs) return false;
	const date = new Date(ms);
	return date.getTime() === ms;
}


/**
 * Create and download the CSV file
 */
function downloadCSV(csvContent: string, fileName: string, encoding: string): void {
	// Create a Blob with the CSV content
	const blob = new Blob([csvContent], {type: `text/csv;charset=${encoding}`});

	// Create a download link
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.setAttribute('href', url);
	link.setAttribute('download', fileName);
	link.style.visibility = 'hidden';

	// Add the link to the DOM and trigger the download
	document.body.appendChild(link);
	link.click();

	// Clean up
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
}

/**
 * Export data from HTML table element to CSV
 * @param tableElement HTML table element to export
 * @param options Export options
 */
export function exportTableToCSV(
	tableElement: HTMLTableElement,
	options: CSVExportOptions = {}
): void {
	const rows = Array.from(tableElement.querySelectorAll('tr'));

	// Extract headers from the first row
	const headers = Array.from(rows[0].querySelectorAll('th, td'))
		.map(cell => cell.textContent?.trim() || '');

	// Extract data rows
	const data = rows.slice(1).map(row => {
		return Array.from(row.querySelectorAll('td'))
			.map(cell => cell.textContent?.trim() || '');
	});

	// Generate column definitions
	const columns: ColumnDefinition[] = headers.map((title, index) => ({
		field: `col${index}`,
		title
	}));

	// Convert data to objects
	const dataObjects = data.map(rowData => {
		const obj: Record<string, string> = {};
		rowData.forEach((cellData, index) => {
			obj[`col${index}`] = cellData;
		});
		return obj;
	});

	// Export to CSV
	exportToCSV(dataObjects, columns, options);
}
